// useNotifications.js
//
// TanStack Query (react-query 3.39.3) data layer for the notification center.
// Reads the LIVE, already-populated backend feed
//   GET api/v1/notification/?page=&page_size=10   (paginated: count/total_pages/results)
// via the existing services/api.getNotifications, and exposes a mark-all-read
// mutation via services/api.markAllRead. Gated by NOTIF_CENTER_ENABLED: when the
// flag is OFF the query is `enabled:false` so it never fires (header stays
// byte-identical to today and the legacy GalleryHeader poll remains the only
// fetch).
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "react-query";
import {
  getNotifications,
  markAllRead,
  markNotificationRead,
  getUnreadNotificationCount,
  deleteNotification,
} from "../../services/api";
import { NOTIF_CENTER_ENABLED } from "../../constants/featureFlags";
import { useFeedGateOpen } from "../../utils/feedFirstPaintGate";

export const NOTIFICATIONS_QUERY_KEY = "notifications";
export const NOTIFICATIONS_INFINITE_KEY = "notifications-infinite";
export const NOTIFICATIONS_UNREAD_KEY = "notifications-unread-count";

// Number of unread items in a results array (null-safe).
export function countUnread(results) {
  if (!Array.isArray(results)) return 0;
  return results.filter((n) => n && !n.is_read).length;
}

// Server-authoritative unread total.
//
// countUnread() above only sees the page the caller fetched (page_size=10), so a
// user with more than 10 unread items reads a badge that is silently wrong —
// MEASURED live on web-stg 2026-07-30 (bundle main.696a0610.js): the bell said
// "Notifications, 10 unread" while GET /api/v1/notification/unread-count/
// returned 26. This hook reads that endpoint so the badge states the truth.
// Callers fall back to countUnread(results) while it loads or if it fails, so the
// badge never regresses to nothing.
// The single rule for what number the unread badge shows. Exported (rather than
// inlined in NotificationCenter) so every surface that renders an unread badge
// resolves it identically and so it is directly testable: the defect this
// replaces was a page-limited count silently masquerading as a total.
//
// Prefer the server total. Fall back to counting the fetched page only while the
// count query is loading or if it failed — a wrong-but-nonzero badge beats a
// badge that vanishes, but the fallback must never win over a real server value,
// INCLUDING a legitimate 0.
export function resolveUnreadBadge(serverUnread, results) {
  return typeof serverUnread === "number"
    ? serverUnread
    : countUnread(results);
}

export function useUnreadNotificationCount(options = {}) {
  const { enabled: callerEnabled = true, ...rest } = options;
  // T22.201: wait for Discover page 1 (utils/feedFirstPaintGate.js).
  const feedGateOpen = useFeedGateOpen();
  return useQuery(
    [NOTIFICATIONS_UNREAD_KEY],
    async () => {
      const res = await getUnreadNotificationCount();
      const payload = (res && res.data) || {};
      const data = payload.data || payload;
      const n = data && data.unread_count;
      return typeof n === "number" ? n : 0;
    },
    {
      enabled: NOTIF_CENTER_ENABLED && callerEnabled && feedGateOpen,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      // kelly-notif-stale-time-ago-0915: with refetchOnWindowFocus off and no
      // other trigger, a tab left open never refetched again after the initial
      // load. Bound the staleness window so a long-lived tab still reconciles
      // periodically instead of only on remount.
      refetchInterval: 60 * 1000,
      ...rest,
    }
  );
}

export function useNotifications(page = 1, options = {}) {
  const { enabled: callerEnabled = true, ...rest } = options;
  // T22.201: wait for Discover page 1 (utils/feedFirstPaintGate.js).
  const feedGateOpen = useFeedGateOpen();
  return useQuery(
    [NOTIFICATIONS_QUERY_KEY, page],
    async () => {
      const res = await getNotifications(page);
      // Axios response -> the paginated payload {count,total_pages,results,...}
      return res && res.data ? res.data : { count: 0, results: [] };
    },
    {
      enabled: NOTIF_CENTER_ENABLED && callerEnabled && feedGateOpen,
      keepPreviousData: true,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      // kelly-notif-stale-time-ago-0915: see useUnreadNotificationCount above —
      // bound the staleness window instead of relying solely on remount.
      refetchInterval: 60 * 1000,
      ...rest,
    }
  );
}

// ISP-9999: shared continuation check for the notification feed's paginated
// payload ({count,total_pages,results,next?}). `next` is the explicit server
// signal when present; some responses omit it entirely (see
// useNotifications.noLivePush.test.js's {count,total_pages,results} fixture —
// no `next` key), so we fall back to comparing the page just fetched against
// total_pages. Shared by useInfiniteNotifications below AND the full
// /notifications page's manual "See More" fetch (pages/Notifications/index.jsx)
// so both surfaces agree on when the feed actually ends — a page-local
// `next != null` check (with no total_pages fallback) reads `undefined` as
// "no next page" and caps pagination at page 1 the moment `next` is absent,
// which is exactly the MEASURED bug (437-notification account, only the first
// 10 ever reachable).
export function hasMoreNotificationPages(payload, currentPage) {
  if (!payload) return false;
  if ("next" in payload) return payload.next != null;
  if (typeof payload.total_pages === "number") {
    return currentPage < payload.total_pages;
  }
  return false;
}

// QA-1014: FB-style infinite feed for the bell dropdown. The dropdown IS the full
// list now (the separate /notifications page is removed), so "See previous
// notifications" appends the next page onto the SAME accumulated list — it never
// resets to page 1 or scrolls back to the top, because react-query keeps every
// fetched page in one `data.pages` array and we only ever push a new page onto the
// end. Callers flatten `data.pages[].results` to render the running list.
//
// getNextPageParam returns the NEXT page number, or undefined when the server says
// there is no next page (`next` null) or we've reached total_pages — undefined is
// react-query's signal that hasNextPage is false.
export function useInfiniteNotifications(options = {}) {
  const { enabled: callerEnabled = true, ...rest } = options;
  // T22.201: wait for Discover page 1 (utils/feedFirstPaintGate.js).
  const feedGateOpen = useFeedGateOpen();
  return useInfiniteQuery(
    [NOTIFICATIONS_INFINITE_KEY],
    async ({ pageParam = 1 }) => {
      const res = await getNotifications(pageParam);
      const payload = (res && res.data) || { count: 0, results: [] };
      // Carry the page we actually fetched so getNextPageParam can increment it
      // without depending on the server echoing a page number.
      return { ...payload, page: pageParam };
    },
    {
      enabled: NOTIF_CENTER_ENABLED && callerEnabled && feedGateOpen,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return undefined;
        const current = lastPage.page || 1;
        return hasMoreNotificationPages(lastPage, current)
          ? current + 1
          : undefined;
      },
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      ...rest,
    }
  );
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation(() => markAllRead(), {
    onSuccess: () => {
      // ISP-9999: the bell badge (NotificationCenter) reads its number from
      // resolveUnreadBadge(serverUnread, results), where serverUnread is this
      // exact [NOTIFICATIONS_UNREAD_KEY] query's cached data. Invalidating it
      // only SCHEDULES a refetch of GET /api/v1/notification/unread-count/ —
      // the badge stays at the pre-mark-all count for however long that round
      // trip takes (MEASURED live: still "2" at 400ms and 1500ms on a loaded
      // backend). Writing 0 into the same cache the badge reads makes the
      // clear synchronous with the mutation's own success, independent of
      // that GET's latency. invalidateQueries still runs right after so the
      // cache reconciles with server truth in the background.
      queryClient.setQueryData([NOTIFICATIONS_UNREAD_KEY], 0);
      queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_INFINITE_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_UNREAD_KEY]);
    },
  });
}

// QA-1014: delete ONE notification from the FB-style kebab menu. Optimistically
// removes the row from every fetched infinite page so it disappears immediately,
// then invalidates on settle to reconcile with the server (the DELETE is a real
// ModelViewSet destroy — the row is gone from the next fetch too). Errors roll the
// optimistic removal back so a failed delete never silently drops the row.
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation((id) => deleteNotification(id), {
    onMutate: async (id) => {
      await queryClient.cancelQueries([NOTIFICATIONS_INFINITE_KEY]);
      const previous = queryClient.getQueryData([NOTIFICATIONS_INFINITE_KEY]);
      queryClient.setQueryData([NOTIFICATIONS_INFINITE_KEY], (prev) => {
        if (!prev || !Array.isArray(prev.pages)) return prev;
        return {
          ...prev,
          pages: prev.pages.map((pg) =>
            pg && Array.isArray(pg.results)
              ? { ...pg, results: pg.results.filter((n) => n && n.id !== id) }
              : pg
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx && ctx.previous !== undefined) {
        queryClient.setQueryData([NOTIFICATIONS_INFINITE_KEY], ctx.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries([NOTIFICATIONS_INFINITE_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_UNREAD_KEY]);
    },
  });
}

// P5S1-O12: mark a SINGLE notification read (used when the user activates a row).
// Optimistically flips is_read in EVERY cached page so the item's unread dot and
// the bell badge clear immediately — the row is about to navigate away, so a full
// refetch would be wasted; the server call persists it for the next fetch. Errors
// are best-effort (a failed mark never blocks navigation); on settle we invalidate
// to reconcile with server truth.
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation((id) => markNotificationRead(id), {
    onMutate: (id) => {
      // Update every cached notifications page ([key, 1], [key, 2], …) by prefix.
      queryClient.setQueriesData([NOTIFICATIONS_QUERY_KEY], (prev) => {
        if (!prev || !Array.isArray(prev.results)) return prev;
        return {
          ...prev,
          results: prev.results.map((n) =>
            n && n.id === id ? { ...n, is_read: true } : n
          ),
        };
      });
      // QA-1014: the live dropdown now reads the infinite cache — flip is_read in
      // every accumulated page there too so the row's unread tint clears at once.
      queryClient.setQueryData([NOTIFICATIONS_INFINITE_KEY], (prev) => {
        if (!prev || !Array.isArray(prev.pages)) return prev;
        return {
          ...prev,
          pages: prev.pages.map((pg) =>
            pg && Array.isArray(pg.results)
              ? {
                  ...pg,
                  results: pg.results.map((n) =>
                    n && n.id === id ? { ...n, is_read: true } : n
                  ),
                }
              : pg
          ),
        };
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_INFINITE_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_UNREAD_KEY]);
    },
  });
}
